import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { User } from '@/types/user';
import { configureAuthBridge } from '@/api/client';

/**
 * Session hint — written to localStorage after the first successful login and
 * cleared on logout. Used by `useSilentRestore` to skip the cold-load
 * `/auth/refresh` call entirely when we know the user has never logged in on
 * this device. This eliminates the otherwise-unavoidable 401 console error
 * for anonymous visitors.
 */
const SESSION_HINT_KEY = 'qxk:session-hint';

export function hasSessionHint(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(SESSION_HINT_KEY) === '1';
  } catch {
    // localStorage may throw in restricted contexts (private mode quotas, SSR, etc.)
    return false;
  }
}

function setSessionHint(): void {
  try {
    window.localStorage.setItem(SESSION_HINT_KEY, '1');
  } catch { /* ignore */ }
}

function clearSessionHint(): void {
  try {
    window.localStorage.removeItem(SESSION_HINT_KEY);
  } catch { /* ignore */ }
}

/**
 * Auth Store — token in MEMORY ONLY (PRD §13.1 security checklist).
 *
 * The access token never touches localStorage or sessionStorage. On hard
 * reload, the page calls `usersApi.getMe()` (which uses the HttpOnly refresh
 * cookie via the response interceptor's single-flight refresh path) to
 * silently restore the session. If that 401s, the user is logged out.
 */

interface AuthState {
  accessToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isHydrating: boolean;

  setAuth: (token: string, user: User) => void;
  setAccessToken: (token: string) => void;
  setUser: (user: User) => void;
  setHydrating: (v: boolean) => void;
  clearAuth: () => void;
  hasRole: (role: User['role']) => boolean;
}

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector((set, get) => ({
    accessToken: null,
    user: null,
    isAuthenticated: false,
    isHydrating: true,

    setAuth: (token, user) => {
      setSessionHint();
      set({ accessToken: token, user, isAuthenticated: true, isHydrating: false });
    },

    setAccessToken: (token) => {
      setSessionHint();
      set({ accessToken: token, isAuthenticated: true });
    },

    setUser: (user) => set({ user, isAuthenticated: true }),

    setHydrating: (v) => set({ isHydrating: v }),

    clearAuth: () => {
      clearSessionHint();
      set({ accessToken: null, user: null, isAuthenticated: false, isHydrating: false });
    },

    hasRole: (role) => get().user?.role === role,
  }))
);

// Wire the api/client.ts auth bridge — runs once at module-load. Idempotent.
configureAuthBridge({
  getAccessToken: () => useAuthStore.getState().accessToken,
  setAccessToken: (token) => useAuthStore.getState().setAccessToken(token),
  onAuthFailure: () => {
    useAuthStore.getState().clearAuth();
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      // Use replace so the back button doesn't return to a 401'd page.
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.replace(`/login?next=${next}`);
    }
  },
});

/** Selector helpers — keep components subscribed to minimal slices. */
export const selectIsAuthenticated = (s: AuthState) => s.isAuthenticated;
export const selectUser = (s: AuthState) => s.user;
export const selectIsAdmin = (s: AuthState) => s.user?.role === 'ADMIN';
